import React from 'react';
import { Button, Input, Modal, Popover, Select, Spin, Table, Tooltip, message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import CommonTable from 'components/CommonTable';
import {
    getInsuranceInvoiceDetail,
    getInsuranceInvoicePdfLink,
    getInsuranceInvoices,
    getInsuranceInvoicesSummary,
} from 'api/financials';
import type {
    InvoiceDetailVo,
    InvoiceLineVo,
    InvoiceListItemVo,
    InvoiceSummaryVo,
    TInvoiceSortBy,
} from 'types/financials';
import { formatDateMDY, formatMoney } from 'pages/dashboard/InsuranceBillingPage/Dashboard/statusConfig';
import s from './s.module.less';

const { Option } = Select;

// PDF 置灰 tooltip 与下载失败共用同一句话（对用户是同一件事：文件拿不到）；
// 不透传后端 message（91023/91022 的措辞对下载场景有误导），不给重试按钮
const NO_PDF_MSG =
    'PDF not available yet. Open the invoice to see the full breakdown, or contact support if you need the file.';

// 折扣金额展示（原型 insNeg）：负数显示 -$xx.xx，0 显示 $0.00
const formatDiscount = (n?: number): string => (n ? `-${formatMoney(Math.abs(n))}` : formatMoney(0));

// 费率为 4 位小数（0.039 → 3.9%）；直接乘 100 有浮点尾巴，先 toFixed 再去零
const formatRate = (rate?: number, _item?: InvoiceLineVo): string => {
    ///** null or "PERCENT" → rate is a fraction (0.039 = 3.9%); "FLAT" → rate is a dollar amount. */
    if (_item && _item.feeType) {
        if (_item.feeType === 'PERCENT') {
            return rate == null ? '—' : `${+(rate * 100).toFixed(2)}%`;
        } else {
            return rate == null ? '—' : `${formatMoney(rate)}`;
        }
    } else if (rate && rate > 1) {
        return `${formatMoney(rate)}`;
    } else {
        return rate == null ? '—' : `${+(rate * 100).toFixed(2)}%`;
    }
    return '—';
};

const formatPeriod = (from?: string, to?: string): string => `${formatDateMDY(from)} - ${formatDateMDY(to)}`;

// 状态徽标：与后端 status 原值对齐（INITIAL/SENT → Pending，PAID → Settled），不做逾期推导
const badgeOf = (inv: { status?: string }) => {
    if (inv.status === 'PAID') {
        return {
            label: 'Settled',
            cls: s.badgeGreen,
            tip: 'You paid this invoice. Nothing more is owed for this month.',
        };
    }
    return { label: 'Pending', cls: s.badgeAmber, tip: 'Invoice issued for this month. Pay it by the due date.' };
};

const StatusBadge: React.FC<{ invoice: InvoiceListItemVo }> = ({ invoice }) => {
    const b = badgeOf(invoice);
    return (
        <div className={`${s.badge} ${b.cls}`}>
            {b.label}
            <Popover content={<div className={s.pop}>{b.tip}</div>} title={null} placement="right" trigger="hover">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M5.48511 6C5.85118 5.22321 6.83895 4.66667 8.00004 4.66667C9.4728 4.66667 10.6667 5.5621 10.6667 6.66667C10.6667 7.59963 9.81496 8.38338 8.66285 8.6044C8.30125 8.67377 8.00004 8.96514 8.00004 9.33333M8 11.3333H8.00667M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Popover>
        </div>
    );
};

// 卡片样式对齐兄弟 tab（CredentialingDeposits 的 overviewSectionItem）：白卡无边框、label + 右侧 info Popover、24px 金额
const MetricCard: React.FC<{ label: string; tip: string; value: string }> = ({ label, tip, value }) => (
    <div className={s.metricCard}>
        <div className={s.metricLabel}>
            <span>{label}</span>
            <Popover trigger="hover" content={<div className={s.pop}>{tip}</div>}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Popover>
        </div>
        <div className={s.metricValue}>{value}</div>
    </div>
);

// 状态漏斗选项按徽标语义分组，值为后端 status 集合
const STATUS_FILTERS = [
    { text: 'Pending', value: 'INITIAL,SENT' },
    { text: 'Settled', value: 'PAID' },
];

// enrolled practice 的 Insurance billing 只有这一个面板（2026-09-16 拍板：enrolled 的
// check/advanced search 都免费，Eligibility checks 子 tab 已移除）
const InsuranceBillingFees: React.FC = () => {
    // 筛选（months/keyword/status 变化时列表与 summary 都要重发；分页排序只刷列表）
    const [months, setMonths] = React.useState(1);
    const [keyword, setKeyword] = React.useState('');
    const [statusSel, setStatusSel] = React.useState<string[]>([]);
    const [sortBy, setSortBy] = React.useState<TInvoiceSortBy>('issueDate');
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
    const [page, setPage] = React.useState(1);
    const [limit, setLimit] = React.useState(20);
    // 明细弹窗把账单删了/失效时的列表强刷
    const [refreshKey, setRefreshKey] = React.useState(0);

    const [list, setList] = React.useState<InvoiceListItemVo[]>([]);
    const [total, setTotal] = React.useState(0);
    const [listLoading, setListLoading] = React.useState(true);
    const [loadFailed, setLoadFailed] = React.useState(false);
    const [summary, setSummary] = React.useState<InvoiceSummaryVo | null>(null);

    const [detailOpen, setDetailOpen] = React.useState(false);
    const [detail, setDetail] = React.useState<InvoiceDetailVo | null>(null);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const downloadingRef = React.useRef(false);

    const statusStr = statusSel.join(',');

    React.useEffect(() => {
        let cancelled = false;
        setListLoading(true);
        (async () => {
            let items: InvoiceListItemVo[] | null = null;
            let metaTotal = 0;
            try {
                const res: any = await getInsuranceInvoices({
                    months,
                    keyword: keyword || undefined,
                    status: statusStr || undefined,
                    sortBy,
                    sortDir,
                    page,
                    limit,
                });
                if (!res?.error) {
                    // 仅列表接口是双层结构：信封 data 里再嵌 data + meta
                    items = res?.data?.data?.data ?? [];
                    metaTotal = res?.data?.data?.meta?.total ?? 0;
                }
            } catch (e) {
                items = null;
            }
            if (cancelled) return;
            setLoadFailed(items == null);
            setList(items || []);
            setTotal(metaTotal);
            setListLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [months, keyword, statusStr, sortBy, sortDir, page, limit, refreshKey]);

    // 指标卡口径是整个筛选区间（不是当前页），必须走 summary 接口且与列表同参，不要拿列表数据自己加总
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            let vo: InvoiceSummaryVo | null = null;
            try {
                const res: any = await getInsuranceInvoicesSummary({
                    months,
                    keyword: keyword || undefined,
                    status: statusStr || undefined,
                });
                if (!res?.error) vo = res?.data?.data ?? null;
            } catch (e) {
                vo = null;
            }
            if (cancelled) return;
            setSummary(vo);
        })();
        return () => {
            cancelled = true;
        };
    }, [months, keyword, statusStr, refreshKey]);

    const openDetail = async (invoiceId: number) => {
        setDetailOpen(true);
        setDetail(null);
        setDetailLoading(true);
        let vo: InvoiceDetailVo | null = null;
        try {
            const res: any = await getInsuranceInvoiceDetail(invoiceId);
            if (!res?.error) vo = res?.data?.data ?? null;
        } catch (e) {
            vo = null;
        }
        if (!vo) {
            // 列表数据过期/账单不属于当前 practice（91023 等）：固定文案 + 刷新列表，不透传后端 message
            setDetailOpen(false);
            setDetailLoading(false);
            message.error('This invoice is no longer available. The list has been refreshed.');
            setRefreshKey((k) => k + 1);
            return;
        }
        setDetail(vo);
        setDetailLoading(false);
    };

    const handleDownload = async (invoiceId: number) => {
        if (downloadingRef.current) return;
        downloadingRef.current = true;
        try {
            // 短链 900 秒过期，每次点击现取，不缓存进 state
            let url: string | undefined;
            try {
                const res: any = await getInsuranceInvoicePdfLink(invoiceId);
                if (!res?.error) url = res?.data?.data?.url;
            } catch (e) {
                url = undefined;
            }
            if (!url) {
                // 任何失败都当作「PDF 还没准备好」（见接口文档 §6.3），无重试按钮
                message.error(NO_PDF_MSG);
                return;
            }
            // 链接已带 Content-Disposition: attachment，直接触发即下载
            const a = document.createElement('a');
            a.href = url;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } finally {
            downloadingRef.current = false;
        }
    };

    const DownloadAction: React.FC<{
        invoice: { invoiceId: number; pdfAvailable?: boolean };
        text: string;
        className: string;
    }> = ({ invoice, text, className }) => {
        // pdfAvailable=false：置灰不隐藏（PDF 由后置任务生成，正常就有时间差且可能漏）；
        // 文案陈述现状不承诺结果，主语是 PDF 不是 invoice
        if (invoice.pdfAvailable === false) {
            return (
                <Tooltip title={NO_PDF_MSG}>
                    <span className={`${className} ${s.linkDisabled}`}>
                        <DownloadOutlined /> {text}
                    </span>
                </Tooltip>
            );
        }
        return (
            <span
                className={className}
                onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(invoice.invoiceId);
                }}
            >
                <DownloadOutlined /> {text}
            </span>
        );
    };

    const handleTableChange = (
        pag: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<InvoiceListItemVo> | SorterResult<InvoiceListItemVo>[],
    ) => {
        setKeyword(String(filters.invoiceNumber?.[0] ?? '').trim());
        setStatusSel((filters.status as string[]) || []);
        const s1 = Array.isArray(sorter) ? sorter[0] : sorter;
        if (s1?.order) {
            setSortBy((s1.columnKey as TInvoiceSortBy) || 'issueDate');
            setSortDir(s1.order === 'ascend' ? 'asc' : 'desc');
        } else {
            setSortBy('issueDate');
            setSortDir('desc');
        }
        // antd 在筛选变化时自动把 current 重置为 1
        setPage(pag.current || 1);
        setLimit(pag.pageSize || 20);
    };

    const columns: ColumnsType<InvoiceListItemVo> = [
        {
            // 完整编号是系统身份证（PDF、后台、客服工单都引用它），原样渲染、不要截短
            title: 'INVOICE #',
            key: 'invoiceNumber',
            dataIndex: 'invoiceNumber',
            filteredValue: keyword ? [keyword] : null,
            filterIcon: (filtered: boolean) => <SearchOutlined style={filtered ? { color: '#0E62F4' } : undefined} />,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters: clear }) => (
                <div className={s.searchDropdown}>
                    <Input
                        placeholder="Search invoice #"
                        value={(selectedKeys[0] as string) || ''}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        allowClear
                    />
                    <div className={s.searchDropdownBtns}>
                        <Button
                            size="small"
                            onClick={() => {
                                clear?.();
                                confirm();
                            }}
                        >
                            Reset
                        </Button>
                        <Button type="primary" size="small" onClick={() => confirm()}>
                            Search
                        </Button>
                    </div>
                </div>
            ),
        },
        {
            title: 'BILLING PERIOD',
            key: 'billingPeriod',
            className: s.mut,
            sorter: true,
            render: (_, inv) => formatPeriod(inv.billingFrom, inv.billingTo),
        },
        {
            title: 'REIMBURSED',
            key: 'reimbursed',
            dataIndex: 'reimbursed',
            align: 'right',
            className: s.mut,
            sorter: true,
            render: (n?: number) => formatMoney(n),
        },
        {
            title: "KLARITY'S DISCOUNT",
            key: 'discount',
            dataIndex: 'discount',
            align: 'right',
            sorter: true,
            render: (n?: number) => formatDiscount(n),
        },
        {
            title: 'YOUR FEE',
            key: 'fee',
            dataIndex: 'fee',
            align: 'right',
            sorter: true,
            render: (n?: number) => formatMoney(n),
        },
        {
            title: 'STATUS',
            key: 'status',
            filters: STATUS_FILTERS,
            filteredValue: statusSel.length ? statusSel : null,
            render: (_, inv) => <StatusBadge invoice={inv} />,
        },
        {
            title: 'ISSUED',
            key: 'issueDate',
            dataIndex: 'issueDate',
            className: s.mut,
            sorter: true,
            defaultSortOrder: 'descend',
            render: (v?: string) => formatDateMDY(v),
        },
        {
            title: 'ACTION',
            key: 'action',
            fixed: 'right',
            align: 'center',
            width: 170,
            render: (_, inv) => (
                <span className={s.actions}>
                    <span
                        className={s.link}
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetail(inv.invoiceId);
                        }}
                    >
                        View
                    </span>
                    <DownloadAction invoice={inv} text="Download" className={s.link} />
                </span>
            ),
        },
    ];

    const lineColumns: ColumnsType<InvoiceLineVo> = [
        // patientName 后端已脱敏（"Linda P*"），直接渲染
        { title: 'CLIENT', dataIndex: 'patientName' },
        { title: 'PAYER', dataIndex: 'payerName', className: s.mut },
        { title: 'SERVICE DATE', dataIndex: 'serviceDate', className: s.mut, render: (v?: string) => formatDateMDY(v) },
        { title: 'DATE PAID', dataIndex: 'paidDate', className: s.mut, render: (v?: string) => formatDateMDY(v) },
        {
            title: 'REIMBURSED',
            dataIndex: 'reimbursed',
            align: 'right',
            className: s.mut,
            render: (n?: number) => formatMoney(n),
        },
        {
            title: 'REGULAR RATE',
            dataIndex: 'regularRate',
            align: 'right',
            className: s.mut,
            render: (rate?: number, item: InvoiceLineVo) => <span>{formatRate(rate, item)}</span>,
        },
        {
            title: 'YOUR RATE',
            dataIndex: 'providerRate',
            align: 'right',
            render: (rate?: number, item: InvoiceLineVo) => (
                <span className={s.yourRate}>{formatRate(rate, item)}</span>
            ),
        },
        {
            title: "KLARITY'S DISCOUNT",
            dataIndex: 'discount',
            align: 'right',
            render: (n?: number) => formatDiscount(n),
        },
        { title: 'YOUR FEE', dataIndex: 'fee', align: 'right', render: (n?: number) => formatMoney(n) },
    ];

    // 空列表两种原因要区分（对齐 CredentialingDeposits）：
    // 无 keyword/status 筛选且没数据 → 指标卡与表格换成空态（标题 + 时间选择器保留，时间区间可随时切换）；
    // 有筛选返回空 → 保留表头，给「清空筛选」出路
    const isEmptyData = !listLoading && !loadFailed && !keyword && statusSel.length === 0 && list.length === 0;
    const emptyText: React.ReactNode = loadFailed ? (
        'Failed to load invoices. Please try again later.'
    ) : (
        <span>No invoices match your filters.</span>
    );

    return (
        <div className={s.wrap}>
            {/* 顶行：子 tab 按钮在左，时间选择器同一行靠右（选择器同时控制卡片与列表的口径），
                无数据时也保留 */}
            <div className={s.subNav}>
                <button type="button" className={s.activeNav}>
                    Managed billing fee
                </button>
                <div className={s.periodSelect}>
                    <Select
                        value={months}
                        onChange={(m) => {
                            setMonths(m);
                            setPage(1);
                        }}
                        style={{ minWidth: 160 }}
                        dropdownStyle={{ minWidth: 160 }}
                    >
                        {[1, 2, 3, 4, 5, 6].map((m) => (
                            <Option key={m} value={m}>
                                {`Last ${m} month${m > 1 ? 's' : ''}`}
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>

            {isEmptyData ? (
                <div className={s.emptyState}>
                    <h2>No invoices yet</h2>
                    <p>
                        Once insurance reimbursements are collected, Klarity generates your invoice at the start of the
                        next month.
                    </p>
                </div>
            ) : (
                <>
                    <div className={s.metrics}>
                        <MetricCard
                            label="Reimbursement collected"
                            tip="Total payer reimbursement collected through Klarity Managed Billing across these invoices."
                            value={summary ? formatMoney(summary.reimbursedTotal ?? 0) : '—'}
                        />
                        <MetricCard
                            label="Managed billing fees"
                            tip="Total managed billing fee across these invoices, based on your agreed rate."
                            value={summary ? formatMoney(summary.feeTotal ?? 0) : '—'}
                        />
                        <MetricCard
                            label="Your discount"
                            tip="How much you saved versus the standard rate, thanks to your negotiated rate."
                            value={summary ? formatDiscount(summary.discountTotal) : '—'}
                        />
                    </div>

                    <CommonTable<InvoiceListItemVo>
                        rowKey="invoiceId"
                        columns={columns}
                        data={list}
                        loading={listLoading}
                        locale={{ emptyText }}
                        onChange={handleTableChange}
                        pagination={{ current: page, pageSize: limit, total, showSizeChanger: true }}
                        scroll={{ x: 'max-content' }}
                        onRow={(inv) => ({ onClick: () => openDetail(inv.invoiceId), className: s.invoiceRow })}
                    />
                </>
            )}

            <Modal
                open={detailOpen}
                onCancel={() => setDetailOpen(false)}
                footer={null}
                width={960}
                style={{ top: 40 }}
                bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
                title={
                    <div className={s.modalHead}>
                        <div>
                            <div className={s.modalTitle}>Invoice {detail?.invoiceNumber || ''}</div>
                            <div className={s.modalSub}>Reimbursements &amp; managed billing fee</div>
                        </div>
                        {detail && <DownloadAction invoice={detail} text="Download invoice" className={s.modalLink} />}
                    </div>
                }
            >
                {detailLoading && (
                    <div className={s.modalLoading}>
                        <Spin />
                    </div>
                )}
                {!detailLoading && detail && (
                    <>
                        <div className={s.modalMeta}>
                            <div>
                                <span>Billing period</span>
                                {formatPeriod(detail.billingFrom, detail.billingTo)}
                            </div>
                            <div>
                                <span>Issued</span>
                                {formatDateMDY(detail.issueDate)}
                            </div>
                            <div>
                                <span>Due</span>
                                {formatDateMDY(detail.dueDate)}
                            </div>
                            <div>
                                <span>Total fee</span>
                                <strong>{formatMoney(detail.fee)}</strong>
                            </div>
                        </div>
                        <Table<InvoiceLineVo>
                            rowKey={(l, i) => l.claimId ?? i ?? 0}
                            columns={lineColumns}
                            dataSource={detail.lines || []}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            summary={() => (
                                // 合计行用头表金额：每行金额已按分舍入，遍历 lines 求和可能与账单差 1-2 分，
                                // 会和 provider 下载的 PDF 对不上（后端已保证头表与行勾稽一致）
                                <Table.Summary.Row className={s.totalRow}>
                                    <Table.Summary.Cell index={0} colSpan={4} align="right">
                                        Totals
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                        {formatMoney(detail.reimbursed)}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} />
                                    <Table.Summary.Cell index={3} />
                                    <Table.Summary.Cell index={4} align="right">
                                        {formatDiscount(detail.discount)}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">
                                        {formatMoney(detail.fee)}
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </>
                )}
            </Modal>
        </div>
    );
};

export default InsuranceBillingFees;
